import * as Sentry from '@sentry/react';
import {
  ClipboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Alert,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import {
  useForm,
  Controller,
  SubmitHandler,
  FormProvider,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { MapProvider } from 'shared/map-exports';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Capacitor } from '@capacitor/core';
import { EvacZone, EvacZoneStatus, GeoEventFormStatus } from 'shared/types';
import { RelatedWildfire } from 'features/wildfires/wildfireGeoEventData.types';
import { NotificationType } from 'features/notifications/notifications.types';
import { InlineStatus } from 'components/InlineStatus';
import { useConfirm } from 'components/ConfirmDialog';
import { useAuthState } from 'state/useAuthState';
import { EvacuationFormSection } from 'features/evacZones/components/EvacuationFormSection';
import { useGeoEventEvacZones } from 'features/evacZones/hooks/useGeoEventEvacZones';
import {
  getEvacZoneStyleFromGeoEvent,
  parseLatitudeValue,
  parseLongitudeValue,
  removeHTMLTags,
} from 'shared/utils';
import { FormInternalLinks, FormLinks } from 'components/FormLinks';
import { getDateFormatted } from 'shared/dates';
import { useUserRegionsQuery } from 'hooks/useUserRegionsQuery';
import { HttpStatusCode, isAxiosError } from 'axios';
import { MediaForm, getFileTypeFromFile } from 'components/MediaForm';
import { useQueryParams } from 'hooks/useQueryParams';
import { InternalNotesInput } from 'components/InternalNotesInput';
import { PushNotificationPreview } from 'features/notifications/components/PushNotificationPreview';
import { useGeoEventAutocomplete } from 'features/geoEvents/hooks/useGeoEventAutocomplete';
import { useIosKeyboardScroller } from 'hooks/useIosKeyboardScroller';
import { useDebouncedWatch } from 'hooks/useDebouncedWatch';
import { usePermissions } from 'hooks/usePermissions';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { enqueueSnackbar } from 'notistack';
import { NotificationType as NotificationTypeConstant } from 'features/notifications/notifications.constants';
import { WildfireManualDeactivationField } from 'features/wildfires/components/WildfireManualDeactivationField';
import { WildfireDeactivationAlert } from 'features/wildfires/components/WildfireDeactivationAlert';
import { AcreageAndContainmentFields } from 'features/wildfires/components/AcreageAndContainmentFields';
import { PendingEvacZonesError } from 'features/evacZones/components/PendingEvacZonesError';
import { PendingReviewEvacZoneErrorType } from 'features/geoEvents/geoEvent.types';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';
import { getGeoEventRegionsChanged } from 'features/geoEvents/hooks/useGeoEventRegionsChanged';
import { getGeoEventNotificationPreviewState } from 'features/geoEvents/utils/getGeoEventNotificationPreviewState';
import { useInitialGeoEventReport } from 'features/reports/hooks/useInitialGeoEventReport';
import { FormWithoutEnter } from 'components/FormWithoutEnter';
import { useQueryClient } from '@tanstack/react-query';
import { handlePendingReviewZonesErrorOrFallback } from 'features/geoEvents/utils/handlePendingReviewZonesErrorOrFallback';
import {
  ReportMessageField,
  ValidationRule,
} from 'features/reports/components/ReportMessageField';
import { GeoEventFormTabs } from 'features/geoEvents/components/GeoEventFormTabs';
import { InternalFormSection } from 'features/geoEvents/components/InternalFormSection';
import { UrgencySelector } from 'features/geoEvents/components/UrgencySelector';
import {
  getMessageFromGeoEventSaveErrorResponse,
  getRedundancyMessage,
  isGeoEventUpgradedFromSilent,
  provisionalEditIsDisabled,
} from 'features/geoEvents/geoEvent.utils';
import { CriticalAlertConfirmation } from 'features/reports/components/CriticalAlertConfirmation';
import { InitialReportNotificationTypeField } from 'features/reports/components/InitialReportNotificationTypeField';
import { useCanSendCriticalInitialReport } from 'features/geoEvents/hooks/useCanSendCriticalInitialReport';
import { useMarkerEditLock } from 'features/geoEvents/hooks/useMarkerEditLock';
import { useMarkerEditRegionSync } from 'features/geoEvents/hooks/useMarkerEditRegionSync';
import { useMarkerMoveConfirmation } from 'features/geoEvents/hooks/useMarkerMoveConfirmation';
import { useSyncInitialLocationToFormState } from 'features/geoEvents/hooks/useSyncInitialLocationToFormState';
import { useFormBlocker } from 'hooks/useFormBlocker';
import { useRegionsChangeHandler } from 'features/geoEvents/hooks/useRegionsChangeHandler';
import {
  ImageUploadData,
  useImageUpload,
} from 'features/reports/hooks/useImageUpload';
import { useWildfireGeoEvent } from 'features/wildfires/hooks/useWildfireGeoEvent';
import { useProvisionalGeoEventActions } from 'features/provisionalSubmissions/hooks/useProvisionalGeoEventActions';
import { useResetOnInitialValuesChange } from 'features/geoEvents/hooks/useResetOnInitialValuesChange';
import { getIncidentRemountKey } from 'features/geoEvents/utils/getIncidentRemountKey';
import { MapFormLayout } from 'components/MapFormLayout';
import { AutocompleteWithBackdrop } from 'components/AutocompleteWithBackdrop';
import { Loader } from 'components/Loader';
import {
  EvacZoneStatuses,
  GeoEventTypes,
  TEXTAREA_MAX_ROWS,
} from '../../../../constants';
import {
  geoEventFormValuesToData,
  getSubmitButtonKey,
  validateIncidentAddress,
  validateIncidentName,
  validatePrescribedIncident,
  validateComplexIncidentName,
  getWildfireRegionsForEvacZones,
} from './WildfireForm.utils';
import {
  WildfireFormValues,
  WildfireFormProps,
  WildfireFormQueryParams,
} from './WildfireForm.types';
import { useValidationSchema } from './hooks/useValidationSchema';
import { useInitialValues } from './hooks/useInitialValues';
import { WildfireFormMap } from './components/WildfireFormMap';
import { WildfireChangelog } from './components/WildfireChangelog';
import { ReviewEditWildfireChanges } from './components/ReviewEditWildfireChanges';

const preserveFieldsOnReset: (keyof WildfireFormValues)[] = [
  'media',
  'confirmSelectedZones',
  'confirmAdditionalRegions',
  'confirmRemovedRegions',
];

const statusOptions: { transKey: string; value: GeoEventFormStatus }[] = [
  { transKey: 'geoEvent.active', value: 'active' },
  { transKey: 'geoEvent.inactive', value: 'inactive' },
];

const useStyles = makeStyles()((theme) => ({
  capitalizeLabel: {
    textTransform: 'capitalize',
  },
  noPushNotif: {
    fontWeight: theme.typography.fontWeightMedium,
    textAlign: 'center',
  },
  checkboxControl: {
    width: 'fit-content',
  },
  dateTimeLabel: {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(1),
  },
  clearDateButton: {
    // Make "clear" button secondary/grey
    '& button:first-of-type': {
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.grey[300],
      '&:hover': {
        backgroundColor: theme.palette.grey[500],
      },
    },
  },
}));

// Warn reporters about the prescribed keyword in the initial report message only if
// the incident name has been filled in and isPrescribed is false
const getPrescribedValidationRule = (hasName: boolean): ValidationRule => ({
  id: 'prescribed',
  condition: ({ isPrescribed }) =>
    hasName && isPrescribed !== undefined && !isPrescribed,
  messageKey: 'createEditWildfire.inputs.reportMessage.warning',
  regex: /prescribed/gi,
});

export const WildfireForm = (props: WildfireFormProps): JSX.Element => {
  const { wildfire, reviewSubmission } = props;
  const isNewIncident = !wildfire;
  const isInternalTabEnabled = useReleaseFlag('internal_tab_on_incidents');
  const { classes } = useStyles();
  const { t } = useTranslation();
  const {
    permissions: { isRxOnlyReporter, isWildfireStaffReporter },
  } = useAuthState();
  const [queryParams] = useQueryParams<WildfireFormQueryParams>();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const initialLatitude = parseLatitudeValue(queryParams.lat);
  const initialLongitude = parseLongitudeValue(queryParams.lng);
  const initialAddress = decodeURIComponent(queryParams.address ?? '');
  const initialExternalGeoEventId = queryParams.external_geo_event_id
    ? parseInt(queryParams.external_geo_event_id, 10)
    : undefined;

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  useIosKeyboardScroller(scrollContainerRef);

  const { saveGeoEvent } = useWildfireGeoEvent({ id: wildfire?.id });

  const {
    regions: regionOptions,
    isLoading: isLoadingRegions,
    error: regionsError,
  } = useUserRegionsQuery();

  // Circular dependency workaround: validation schema needs evac zones for form initialization,
  // but evac zones depend on form's regions field. Start with empty array, update via useEffect.
  const [validationRegionEvacZones, setValidationRegionEvacZones] = useState<
    EvacZone[]
  >([]);

  const validationSchema = useValidationSchema(
    wildfire,
    validationRegionEvacZones,
    t,
  );

  const prescribedParam = queryParams.prescribed === 'true';
  const initialPrescribed =
    isRxOnlyReporter || prescribedParam || Boolean(wildfire?.data.isPrescribed);
  const initStatusBool = wildfire ? wildfire.isActive : true;
  // todo: refactor the form fields to use true/false boolean, not this active/inactive string literal
  const initialStatus = initStatusBool ? 'active' : 'inactive';

  const initialValues = useInitialValues({
    wildfire,
    reviewSubmittedData:
      reviewSubmission?.submittedData.geoEventType === 'wildfire'
        ? reviewSubmission.submittedData
        : undefined,
    reviewSubmissionMedia: reviewSubmission?.media,
    initialAddress,
    initialStatus,
    initialPrescribed,
    initialLatitude,
    initialLongitude,
    initialAlertCameraId: queryParams.alertCameraId,
    initialExternalGeoEventId,
  });

  const formMethods = useForm<WildfireFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const {
    control,
    formState,
    setValue,
    handleSubmit,
    watch,
    reset,
    getValues,
    setError,
    clearErrors,
  } = formMethods;

  useResetOnInitialValuesChange({
    values: initialValues,
    getValues,
    reset,
    preserveFields: preserveFieldsOnReset,
  });

  const debouncedLat = useDebouncedWatch({ control, name: 'lat' });
  const debouncedLng = useDebouncedWatch({ control, name: 'lng' });

  const { data: permissions } = usePermissions({
    geoEventType: GeoEventTypes.wildfire,
    lat: debouncedLat,
    lng: debouncedLng,
  });
  const geoEventPermission = isNewIncident
    ? permissions?.wildfire?.geoEvent.createPermission
    : permissions?.wildfire?.geoEvent.editPermission;
  const isProvisional =
    !reviewSubmission && geoEventPermission === 'provisional';
  const allowedAssetTypes = isProvisional
    ? permissions?.wildfire?.report.allowedMediaAssetTypes
    : undefined;

  const [userChangedAddress, setUserChangedAddress] = useState(false);
  const [isCriticalConfirmed, setIsCriticalConfirmed] = useState(false);
  const { handleLocationChange } = useSyncInitialLocationToFormState({
    isLoadingRegions,
    regionOptions,
    geoEventType: GeoEventTypes.wildfire,
    geoEvent: wildfire,
    // Do not update the wildfire's address or regions if we're on edit mode.
    canUpdateAddressAndRegion: isNewIncident && !userChangedAddress,
    setValue,
  });

  const [
    name,
    address,
    childWildfires,
    isComplexParent,
    isPrescribed,
    isFps,
    notificationType,
    regions,
    prescribedDateStartLocal,
    activeEvacuations,
    evacZoneStatuses,
    releasePendingZoneUids,
    asset,
    media,
    fileType,
    manualDeactivationStarted,
    acreage,
    containment,
    evacuationOrders,
    evacuationWarnings,
    evacuationAdvisories,
    evacuationShelterInPlace,
    customOrders,
    customWarnings,
    customAdvisories,
    customShelterInPlace,
    temporarilyDisplayEvacZones,
    unstructuredEvacuationRegionIds,
    lat,
    lng,
    reporterManaged,
    reportNotificationType,
  ] = watch([
    'name',
    'address',
    'childWildfires',
    'isComplexParent',
    'isPrescribed',
    'isFps',
    'notificationType',
    'regions',
    'prescribedDateStartLocal',
    'activeEvacuations',
    'evacZoneStatuses',
    'releasePendingZoneUids',
    'asset',
    'media',
    'fileType',
    'manualDeactivationStarted',
    'acreage',
    'containment',
    'evacuationOrders',
    'evacuationWarnings',
    'evacuationAdvisories',
    'evacuationShelterInPlace',
    'customOrders',
    'customWarnings',
    'customAdvisories',
    'customShelterInPlace',
    'temporarilyDisplayEvacZones',
    'unstructuredEvacuationRegionIds',
    'lat',
    'lng',
    'reporterManaged',
    'reportNotificationType',
    'links',
    'internalLinks',
    'reportMessage', // do not remove, used in ReportMessageField component
  ]);

  const { checkbox: markerMoveCheckbox, isBlockingSubmit: markerMoveBlocking } =
    useMarkerMoveConfirmation({
      originalLatLng: wildfire
        ? { lat: wildfire.lat, lng: wildfire.lng }
        : null,
      currentLatLng: { lat, lng },
      isEditing: Boolean(wildfire),
    });

  const canSendCriticalInitialReport = useCanSendCriticalInitialReport({
    isNewIncident,
    isModeratedSubmission: isProvisional || Boolean(reviewSubmission),
    notificationType,
    isParent: isComplexParent,
    reporterManaged,
  });
  const isCriticalInitialReport =
    canSendCriticalInitialReport &&
    reportNotificationType === NotificationType.Critical;

  const {
    regionEvacZones,
    evacZoneStyles,
    isLoadingEvacZones,
    isErrorEvacZones,
    lockEvacuationZonesChangedNotification,
    unlockEvacuationZonesChangedNotification,
    evacZones,
    pendingEvacZones,
  } = useGeoEventEvacZones({
    regions: getWildfireRegionsForEvacZones(regions, wildfire),
    evacZoneStatuses,
    geoEvent: wildfire,
    setValue,
    releasePendingZoneUids,
    temporarilyDisplayEvacZones,
  });

  useEffect(() => {
    // Update the validation region evac zones when regions change
    setValidationRegionEvacZones(regionEvacZones);
  }, [regionEvacZones]);

  const prevChildWildfiresLength = useRef(childWildfires.length);
  useEffect(() => {
    // Repopulate regions with original values when all child incidents are
    // removed from a complex incident, so the user can manage counties directly.
    if (
      prevChildWildfiresLength.current > 0 &&
      childWildfires.length === 0 &&
      wildfire?.regions.length
    ) {
      setValue('regions', wildfire.regions);
    }
    prevChildWildfiresLength.current = childWildfires.length;
  }, [childWildfires.length, wildfire?.regions, setValue]);

  const imgData = useMemo((): ImageUploadData | null => {
    if (!media || !('type' in media) || !fileType) return null;
    return { file: media, fileType };
  }, [fileType, media]);

  const handleImageUploadError = useCallback(() => {
    enqueueSnackbar(t('addIncidentReport.imageUploadError'), {
      variant: 'error',
    });
  }, [t]);

  const {
    uploadImgPromise,
    uploadImageVerification,
    mediaUrl,
    error: uploadImgError,
  } = useImageUpload({
    imgData,
    onError: handleImageUploadError,
    enabled: isNewIncident,
  });

  const { unblockedNavigate } = useFormBlocker({
    shouldBlockNavigation: Boolean(formState.isDirty),
  });

  const { submitForReview, approveSubmission, rejectSubmission, isRejecting } =
    useProvisionalGeoEventActions({
      reviewSubmission,
      geoEvent: wildfire,
      navigate: unblockedNavigate,
    });

  const handleSubmitGeoEvent: SubmitHandler<WildfireFormValues> = async (
    values,
  ): Promise<void> => {
    const data = geoEventFormValuesToData(
      values,
      mediaUrl,
      isCriticalInitialReport,
    );

    if (reviewSubmission) {
      await approveSubmission(data);
      return;
    }

    if (uploadImgPromise) {
      const uploadResult = await uploadImgPromise;
      if (uploadResult instanceof Error) {
        return;
      }

      //  Verify that the upload actually happened
      if (uploadImageVerification) {
        const imageIsUploaded = await uploadImageVerification();
        if (!imageIsUploaded) {
          handleImageUploadError();
          return;
        }
      }
    }

    if (isProvisional) {
      await submitForReview(data);
      return;
    }

    let mutatedGeoEvent = null;
    try {
      lockEvacuationZonesChangedNotification();
      mutatedGeoEvent = await saveGeoEvent({
        updatedGeoEvent: data,
        originalGeoEvent: wildfire,
        shouldRetryOnDuplicate: async (error): Promise<boolean> => {
          const resp = await confirm(getRedundancyMessage(error.response));
          return resp;
        },
      });
    } catch (error: unknown) {
      unlockEvacuationZonesChangedNotification();
      if (!isAxiosError(error)) {
        Sentry.captureException(error);
        enqueueSnackbar(t('common.unknownError'), {
          variant: 'error',
        });
        return;
      }

      const errorMessage = getMessageFromGeoEventSaveErrorResponse(error, t);
      const errorStatus = error.response?.status;

      if (errorStatus === HttpStatusCode.BadRequest) {
        await handlePendingReviewZonesErrorOrFallback({
          error,
          queryClient,
          t,
          currentZonesWithStatus: values.evacZoneStatuses,
          updateFormZones: (zonesWithStatus) =>
            setValue('evacZoneStatuses', zonesWithStatus),
          geoEventId: wildfire?.id,
          fallbackAction: () =>
            enqueueSnackbar(errorMessage, { variant: 'error' }),
        });

        return;
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
    }

    if (!mutatedGeoEvent) {
      // A reporter is editing an existing incident but has made no changes.
      if (wildfire) {
        unblockedNavigate(`/i/${wildfire.id}`);
      }
      return;
    }

    const snackbarMessageKey = `createEditWildfire.successMessage.${
      isNewIncident ? 'create' : 'update'
    }`;
    enqueueSnackbar(t(snackbarMessageKey), { variant: 'success' });

    unblockedNavigate(`/i/${mutatedGeoEvent.id}`);
  };

  const { muiAutocompleteProps: geoEventAutocompleteProps } =
    useGeoEventAutocomplete<RelatedWildfire>({
      enabled: isComplexParent && isWildfireStaffReporter,
      filterGeoEvents: (geoEvents) =>
        geoEvents.filter(
          (ge) =>
            !ge.data.isComplexParent &&
            !ge.data.isPrescribed &&
            ge.id !== wildfire?.id,
        ),
      latLng: initialValues,
      value: childWildfires,
      geoEventTypes: ['wildfire'],
    });

  const changelogInitialRegions = useMemo((): GeoEventRegion[] | undefined => {
    if (!reviewSubmission) return initialValues.regions;
    if (wildfire?.data?.isComplexParent) return [];
    return wildfire?.regions;
  }, [reviewSubmission, wildfire, initialValues.regions]);

  // Same baseline as the changelog, so the "confirm added regions" checkbox and
  // the preview always appear together.
  const { regionsAdded } = useMemo(
    () => getGeoEventRegionsChanged(changelogInitialRegions ?? [], regions),
    [changelogInitialRegions, regions],
  );

  const {
    initialReport,
    isLoading: isLoadingInitialReport,
    isError: isErrorInitialReport,
  } = useInitialGeoEventReport({
    geoEventId: wildfire?.id,
    // SYNC WARNING: mirrors `notify_geo_event_updated` on the backend, which
    // pushes when regions are added and when an incident is upgraded from
    // silent. Only complexes need the fetch, since they are the only wildfires
    // whose push body comes from a report rather than the address.
    enabled:
      isComplexParent &&
      (regionsAdded ||
        isGeoEventUpgradedFromSilent({
          previousNotificationType: wildfire?.notificationType,
          currentNotificationType: notificationType,
        })),
  });

  // SYNC WARNING: mirrors `create_fcm_message` on the backend, where a complex
  // parent's push body is its initial report's message (complexes carry no
  // address), falling back to the address when the complex has no reports. On
  // create that report is the one being submitted; on edit it is the one fetched
  // above. Changing either side without the other makes this preview disagree
  // with what users receive.
  const complexPreviewBody = wildfire
    ? (initialReport?.message ?? address)
    : getValues('reportMessage');

  const previewBody = isComplexParent
    ? removeHTMLTags(complexPreviewBody)
    : address;

  const { showPreviewSection, showPreview, showNoNotificationNote } =
    getGeoEventNotificationPreviewState({
      geoEvent: wildfire,
      currentNotificationType: notificationType,
      regionsAdded,
      title: name,
      body: previewBody,
      isDirty: formState.isDirty,
      sendsNotifications: !isPrescribed,
    });

  const handlePaste: ClipboardEventHandler<HTMLFormElement> = (event) => {
    /**
     * This handles copy-paste on the web.  In order to use,
     * the user must first click on the page to paste
     */
    if (Capacitor.getPlatform() !== 'web' || asset !== 'image') return;

    const clipboardItems = event.clipboardData.items;

    const items: DataTransferItem[] = [].slice
      .call(clipboardItems)
      .filter((item: DataTransferItem) => item.type.indexOf('image') !== -1);

    if (items.length === 0) {
      return;
    }

    const item = items[0];
    // Get the blob of image
    const blob = item.getAsFile();
    if (!blob) return;

    setValue('media', blob, { shouldDirty: true });
    setValue('fileType', getFileTypeFromFile(blob), { shouldDirty: true });
  };

  // Use a boolean flag instead of checking the name string directly.
  // This avoids repeatedly evaluating the rule condition on each keystroke,
  // which improves performance.
  const hasName = Boolean(name.trim());
  const reportCustomValidationRules = useMemo(
    () => [getPrescribedValidationRule(hasName)],
    [hasName],
  );

  const hasStructuredEvacZones = Boolean(evacZones?.length > 0);

  const customEvacZoneStatuses = (
    hasStructuredEvacZones
      ? [
          customOrders && EvacZoneStatuses.orders,
          customWarnings && EvacZoneStatuses.warnings,
          customAdvisories && EvacZoneStatuses.advisories,
          customShelterInPlace && EvacZoneStatuses.shelterInPlace,
        ]
      : [
          removeHTMLTags(evacuationOrders) && EvacZoneStatuses.orders,
          removeHTMLTags(evacuationWarnings) && EvacZoneStatuses.warnings,
          removeHTMLTags(evacuationAdvisories) && EvacZoneStatuses.advisories,
          removeHTMLTags(evacuationShelterInPlace) &&
            EvacZoneStatuses.shelterInPlace,
        ]
  ).filter(Boolean) as EvacZoneStatus[];

  const handleRegionsChange = useRegionsChangeHandler({
    evacZoneStatuses,
    regionEvacZones,
    customEvacZoneStatuses,
    evacZoneStyles,
    unstructuredEvacuationRegionIds,
  });

  // The counties field is emptied for a complex parent with children, so fall
  // back to the incident's regions
  const complexParentRegions =
    isComplexParent && childWildfires.length
      ? (wildfire?.regions ?? [])
      : undefined;

  const markerEditRegionSync = useMarkerEditRegionSync({
    geoEventType: GeoEventTypes.wildfire,
    regionOptions,
    enabled: Boolean(wildfire) && !childWildfires.length,
    handleRegionsChange,
    getValues,
    setValue,
  });

  const markerLock = useMarkerEditLock({
    onCommit: handleLocationChange,
    isEditing: Boolean(wildfire),
    onEnterEditMode: markerEditRegionSync.onEnterEditMode,
    onStage: markerEditRegionSync.onStage,
    onCancel: markerEditRegionSync.onCancel,
  });

  if (isLoadingRegions) {
    return <Loader disableBackground />;
  }

  if (regionsError) {
    return <InlineStatus status="error" fullWidth />;
  }

  const nameWarning = formState.errors.name
    ? undefined
    : validateIncidentName(name, t);
  const addressWarning = formState.errors.address
    ? undefined
    : validateIncidentAddress(address, t);
  const prescribedWarning = formState.errors.name
    ? undefined
    : validatePrescribedIncident(name, isPrescribed, t);
  const complexNameWarning = formState.errors.name
    ? undefined
    : validateComplexIncidentName(name, isComplexParent, t);

  const isSubmittingOrRejecting = formState.isSubmitting || isRejecting;

  const submitButtons = reviewSubmission ? (
    <Grid size={12} container spacing={2}>
      <Grid size={6}>
        <Button
          type="submit"
          fullWidth
          size="large"
          color="success"
          variant="contained"
          disabled={
            isSubmittingOrRejecting ||
            markerMoveBlocking ||
            markerLock.hasStagedChange
          }
        >
          {formState.isSubmitting ? (
            <CircularProgress size={24} />
          ) : (
            t('reviewSubmission.buttons.approve')
          )}
        </Button>
      </Grid>
      <Grid size={6}>
        <Button
          fullWidth
          size="large"
          color="error"
          variant="contained"
          onClick={rejectSubmission}
          disabled={isSubmittingOrRejecting}
        >
          {isRejecting ? (
            <CircularProgress size={24} />
          ) : (
            t('reviewSubmission.buttons.reject')
          )}
        </Button>
      </Grid>
    </Grid>
  ) : (
    <Grid size={12}>
      <Button
        type="submit"
        fullWidth
        size="large"
        disabled={
          formState.isSubmitting ||
          (isNewIncident && uploadImgError) ||
          markerLock.hasStagedChange ||
          markerMoveBlocking ||
          (isCriticalInitialReport && !isCriticalConfirmed) ||
          provisionalEditIsDisabled({
            isProvisional,
            isEditing: !isNewIncident,
            isDirty: formState.isDirty,
            currentValues: getValues(),
            initialValues,
          })
        }
      >
        {formState.isSubmitting ? (
          <CircularProgress size={24} />
        ) : (
          t(
            getSubmitButtonKey({
              isProvisional,
              isEditing: !isNewIncident,
              isScheduled: Boolean(prescribedDateStartLocal),
              isCritical: isCriticalInitialReport,
            }),
          )
        )}
      </Button>
    </Grid>
  );

  const reviewSubmissionCenter = reviewSubmission?.submittedData
    ? {
        lat: reviewSubmission.submittedData.lat ?? initialValues.lat,
        lng: reviewSubmission.submittedData.lng ?? initialValues.lng,
      }
    : undefined;

  const formFields = (
    <Grid container spacing={2} sx={{ width: '100%', minHeight: '100%' }}>
      {!isInternalTabEnabled && (
        <Grid size={12}>
          <Typography variant="h3">
            {t('createEditWildfire.sections.incident')}
          </Typography>
        </Grid>
      )}

      {reviewSubmission && isNewIncident && (
        <Grid size={12}>
          <Alert severity="info">
            {t('reviewSubmission.submittedByCreateGeoEvent', {
              firstName: reviewSubmission.createdBy.firstName,
              lastName: reviewSubmission.createdBy.lastName,
              date: getDateFormatted(reviewSubmission.dateCreated, {
                includeYear: true,
                includeTimeZone: true,
              }),
            })}
          </Alert>
        </Grid>
      )}

      {reviewSubmission && !isNewIncident && (
        <Grid size={12}>
          <ReviewEditWildfireChanges
            wildfire={wildfire!}
            reviewSubmission={reviewSubmission}
            evacZones={regionEvacZones}
            zoneStyle={getEvacZoneStyleFromGeoEvent(wildfire!)}
          />
        </Grid>
      )}

      {isProvisional && (
        <Grid size={12}>
          <Alert severity="info">
            {t('createEditWildfire.provisionalBanner')}
          </Alert>
        </Grid>
      )}

      <Grid size={12}>
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                id="field-control-name"
                label={t('createEditWildfire.inputs.name.label')}
                fullWidth
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ||
                  nameWarning ||
                  prescribedWarning ||
                  complexNameWarning ||
                  t('createEditWildfire.inputs.name.helperText')
                }
                required
                disabled={formState.isSubmitting}
                color={
                  nameWarning || prescribedWarning || complexNameWarning
                    ? 'warning'
                    : undefined
                }
                focused={Boolean(
                  nameWarning || prescribedWarning || complexNameWarning,
                )}
                slotProps={{
                  formHelperText: {
                    sx: {
                      color:
                        nameWarning || prescribedWarning || complexNameWarning
                          ? 'warning.main'
                          : undefined,
                    },
                  },
                }}
              />
            );
          }}
        />
      </Grid>

      <Grid size={12}>
        <Controller
          name="regions"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            return (
              <FormControl fullWidth>
                <AutocompleteWithBackdrop
                  multiple
                  id="regions"
                  groupBy={(region): string => region.state}
                  options={regionOptions}
                  getOptionLabel={(region): string =>
                    `${region.displayName}, ${region.state}`
                  }
                  isOptionEqualToValue={(option, value): boolean =>
                    option.id === value.id
                  }
                  value={field.value}
                  onChange={(_e, newRegions): void => {
                    const validatedRegions = handleRegionsChange(
                      newRegions,
                      field.value,
                    );
                    field.onChange(validatedRegions);
                  }}
                  onBlur={field.onBlur}
                  disabled={
                    formState.isSubmitting || Boolean(childWildfires.length)
                  }
                  renderOption={(
                    optionProps,
                    region,
                    { selected },
                  ): JSX.Element => {
                    const { key, ...otherProps } = optionProps;
                    return (
                      <li key={key} {...otherProps}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          sx={{ marginRight: 1 }}
                          checked={selected}
                        />
                        {region.displayName}
                      </li>
                    );
                  }}
                  renderInput={(params): JSX.Element => (
                    <TextField
                      {...params}
                      name={field.name}
                      inputRef={field.ref}
                      variant="outlined"
                      label={t('editGeoEvent.inputs.counties.label')}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      required={isComplexParent ? !childWildfires.length : true}
                    />
                  )}
                />
              </FormControl>
            );
          }}
        />
      </Grid>

      <Grid size={12}>
        <Controller
          name="address"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                id="field-control-address"
                label={t('createEditWildfire.inputs.address.label')}
                fullWidth
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ||
                  addressWarning ||
                  t('createEditWildfire.inputs.address.helperText')
                }
                required={!isComplexParent}
                disabled={formState.isSubmitting || isComplexParent}
                multiline
                minRows={1}
                maxRows={TEXTAREA_MAX_ROWS}
                color={addressWarning ? 'warning' : undefined}
                focused={Boolean(addressWarning)}
                onChange={(e) => {
                  setUserChangedAddress(Boolean(e.target.value));
                  field.onChange(e);
                }}
                slotProps={{
                  formHelperText: {
                    sx: {
                      color: addressWarning ? 'warning.main' : undefined,
                    },
                  },
                }}
              />
            );
          }}
        />
      </Grid>

      <Grid size={12}>
        <Controller
          name="status"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                select
                id="field-control-status"
                label={t('createEditWildfire.inputs.status.label')}
                fullWidth
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                required
                disabled={
                  formState.isSubmitting ||
                  (isNewIncident && Boolean(prescribedDateStartLocal))
                }
              >
                {statusOptions.map(({ transKey, value }) => {
                  return (
                    <MenuItem
                      key={transKey}
                      value={value}
                      className={classes.capitalizeLabel}
                    >
                      {t(transKey)}
                    </MenuItem>
                  );
                })}
              </TextField>
            );
          }}
        />
      </Grid>

      <Grid size={12}>
        <Controller
          name="notificationType"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                select
                id="field-control-notificationType"
                label={t('createEditWildfire.inputs.notificationType.label')}
                fullWidth
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                required
                className={classes.capitalizeLabel}
                disabled={
                  formState.isSubmitting ||
                  wildfire?.notificationType === NotificationType.Normal ||
                  isPrescribed ||
                  isComplexParent
                }
              >
                {[
                  NotificationTypeConstant.NORMAL,
                  NotificationTypeConstant.SILENT,
                ].map((notifType) => {
                  return (
                    <MenuItem
                      key={notifType}
                      value={notifType}
                      className={classes.capitalizeLabel}
                    >
                      {notifType}
                    </MenuItem>
                  );
                })}
              </TextField>
            );
          }}
        />
      </Grid>

      <Grid size={12}>
        <AcreageAndContainmentFields isPrescribed={Boolean(isPrescribed)} />
      </Grid>

      <Grid size={12}>
        <WildfireDeactivationAlert
          hasEvacZones={Boolean(regionEvacZones.length)}
        />
      </Grid>

      <Grid size={12}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Controller
            name="isFps"
            control={control}
            render={({ field }): JSX.Element => {
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isFps"
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      id="field-control-isFps"
                    />
                  }
                  label={t('createEditWildfire.forwardProgressStopped')}
                  disabled={
                    formState.isSubmitting ||
                    isPrescribed ||
                    isComplexParent ||
                    (Boolean(manualDeactivationStarted) && !field.value)
                  }
                  className={classes.checkboxControl}
                />
              );
            }}
          />

          {!isNewIncident && (
            <WildfireManualDeactivationField
              hasEvacZones={Boolean(regionEvacZones.length)}
            />
          )}

          <Controller
            name="isPrescribed"
            control={control}
            render={({ field }): JSX.Element => {
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isPrescribed"
                      checked={field.value}
                      onChange={(event) => {
                        field.onChange(event.target.checked);
                        if (event.target.checked) {
                          setValue('notificationType', NotificationType.Silent);
                          setValue('containment', null);
                          setValue('isFps', false);
                        } else {
                          setValue('prescribedDateStartLocal', null);
                        }
                      }}
                      id="field-control-isPrescribed"
                    />
                  }
                  label={t('createEditWildfire.prescribedBurn')}
                  disabled={
                    formState.isSubmitting ||
                    !isNewIncident ||
                    isRxOnlyReporter ||
                    isComplexParent
                  }
                  className={classes.checkboxControl}
                />
              );
            }}
          />

          <Controller
            name="isComplexParent"
            control={control}
            render={({ field }): JSX.Element => {
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isComplexParent"
                      checked={field.value}
                      onChange={(event) => {
                        field.onChange(event.target.checked);
                      }}
                      id="field-control-isComplexParent"
                    />
                  }
                  label={t('createEditWildfire.complexIncident')}
                  disabled={
                    formState.isSubmitting ||
                    isProvisional ||
                    notificationType === NotificationType.Silent ||
                    wildfire?.data?.isComplexParent ||
                    Boolean(wildfire?.parentGeoEvents?.length) ||
                    isPrescribed ||
                    !isWildfireStaffReporter ||
                    isFps
                  }
                  className={classes.checkboxControl}
                />
              );
            }}
          />
        </Box>
      </Grid>

      {isComplexParent && (
        <Grid size={12}>
          <Controller
            name="childWildfires"
            control={control}
            render={({ field, fieldState }) => {
              return (
                <FormControl fullWidth>
                  <AutocompleteWithBackdrop
                    {...geoEventAutocompleteProps}
                    value={field.value}
                    onChange={(_, value) => {
                      field.onChange(value);
                    }}
                    onBlur={field.onBlur}
                    disabled={
                      formState.isSubmitting ||
                      isProvisional ||
                      !isWildfireStaffReporter
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name={field.name}
                        inputRef={field.ref}
                        variant="outlined"
                        label={t(
                          'createEditWildfire.inputs.complexIncidents.label',
                        )}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        slotProps={{
                          htmlInput: {
                            ...params.inputProps,
                            'aria-label': t(
                              'createEditWildfire.inputs.complexIncidents.ariaLabel',
                            ),
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              );
            }}
          />
        </Grid>
      )}

      {!isInternalTabEnabled && (
        <>
          <FormLinks />
          <Grid size={12}>
            <Box sx={{ marginTop: 1 }}>
              <Typography variant="h3">
                {t('createEditWildfire.sections.internal')}
              </Typography>
            </Box>
          </Grid>

          {!isPrescribed && !isRxOnlyReporter && (
            <Grid size={12}>
              <UrgencySelector
                disabled={!isWildfireStaffReporter || formState.isSubmitting}
              />
            </Grid>
          )}

          <Grid size={12}>
            <InternalNotesInput
              key={getIncidentRemountKey('internal-notes', wildfire)}
              initialValue={initialValues.reporterOnlyNotes}
            />
          </Grid>

          <FormInternalLinks />
        </>
      )}

      {isPrescribed && (
        <Grid size={12}>
          <Controller
            name="prescribedDateStartLocal"
            control={control}
            render={({ field, fieldState }): JSX.Element => {
              return (
                <FormControl fullWidth data-testid="prescribed-date-start">
                  <label
                    htmlFor="field-control-prescribedDateStart"
                    className={classes.dateTimeLabel}
                  >
                    {t('createEditWildfire.inputs.prescribedDateStart.title')}
                  </label>

                  <DateTimePicker
                    value={field.value}
                    timezone="UTC"
                    onChange={(newValue): void => {
                      field.onChange(newValue);
                    }}
                    // disablePast is only true when creating a new event
                    disablePast={isNewIncident}
                    ampm
                    label={t(
                      'createEditWildfire.inputs.prescribedDateStart.label',
                    )}
                    slotProps={{
                      textField: {
                        id: 'field-control-prescribedDateStart',
                        InputProps: {
                          placeholder: 'MM/DD/YYYY hh:mm aa',
                        },
                        helperText: fieldState.error?.message,
                        error: Boolean(fieldState.error),
                      },
                      actionBar: {
                        actions: ['clear', 'accept'],
                        classes: { root: classes.clearDateButton },
                      },
                    }}
                    format="MM/dd/yyyy hh:mm aa"
                    onError={(errMsg) => {
                      if (errMsg) {
                        setError('prescribedDateStartLocal', {
                          message: t(
                            'createEditWildfire.inputs.prescribedDateStart.invalidError',
                          ),
                        });
                      } else {
                        clearErrors('prescribedDateStartLocal');
                      }
                    }}
                  />
                  <FormHelperText>
                    {t(
                      'createEditWildfire.inputs.prescribedDateStart.helperText',
                    )}
                  </FormHelperText>
                </FormControl>
              );
            }}
          />
        </Grid>
      )}

      {!isNewIncident && !isPrescribed && (
        <Grid size={12}>
          <EvacuationFormSection
            key={getIncidentRemountKey('evacuations', wildfire)}
            isExpanded={activeEvacuations}
            onToggle={() => setValue('activeEvacuations', !activeEvacuations)}
            isSubmitting={formState.isSubmitting}
            evacZones={regionEvacZones}
            zoneStyles={evacZoneStyles}
            isLoading={isLoadingEvacZones}
            isError={isErrorEvacZones}
            incidentRegions={complexParentRegions}
            disabled={isProvisional}
          />
        </Grid>
      )}

      {isNewIncident && (
        <>
          <Grid size={12}>
            <Box sx={{ marginTop: 1 }}>
              <Typography variant="h3">
                {t('createEditWildfire.sections.update')}
              </Typography>
            </Box>
          </Grid>

          <Grid size={12}>
            <InitialReportNotificationTypeField
              label={t(
                'createEditWildfire.inputs.reportNotificationType.label',
              )}
              canSendCritical={canSendCriticalInitialReport}
            />
          </Grid>

          <Grid size={12}>
            <ReportMessageField
              name="reportMessage"
              initialValue={initialValues.reportMessage}
              initialValues={{
                acreage: null,
                containment: null,
              }}
              currentValues={{
                acreage,
                containment,
                isFps,
                isPrescribed,
                notificationType,
              }}
              helperText={t(
                'createEditWildfire.inputs.reportMessage.helperText',
              )}
              customValidationRules={reportCustomValidationRules}
              isCritical={isCriticalInitialReport}
            />
          </Grid>
        </>
      )}

      {isNewIncident && (
        <MediaForm
          latFieldName="mediaLat"
          lngFieldName="mediaLng"
          azFieldName="mediaAz"
          geoEventType={GeoEventTypes.wildfire}
          allowedAssetTypes={allowedAssetTypes}
          mediaAttribution={reviewSubmission?.media?.[0]?.attribution}
          isReviewing={Boolean(reviewSubmission)}
        />
      )}

      {isInternalTabEnabled && <FormLinks />}

      {showPreviewSection && (
        <Grid size={12}>
          <Box sx={{ marginTop: 3 }}>
            <Typography variant="h3">
              {t('createEditWildfire.sections.preview')}
            </Typography>
          </Box>
        </Grid>
      )}

      {showPreview && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <PushNotificationPreview
            title={name}
            body={previewBody}
            isBodyLoading={isLoadingInitialReport}
            isBodyError={isErrorInitialReport}
            isCritical={isCriticalInitialReport}
          />
        </Grid>
      )}

      {showNoNotificationNote && (
        <Grid size={12}>
          <Typography className={classes.noPushNotif}>
            {t('createEditWildfire.noPushNotification')}
          </Typography>
        </Grid>
      )}

      {!isNewIncident && (
        <Grid size={12}>
          <WildfireChangelog
            wildfire={wildfire}
            evacZones={regionEvacZones}
            zoneStyle={getEvacZoneStyleFromGeoEvent(wildfire)}
            initialRegions={changelogInitialRegions}
            notifiesRegionChanges={!isPrescribed}
            isProvisional={isProvisional}
          />
        </Grid>
      )}

      {formState.errors.evacZoneStatuses?.message && (
        <Grid size={12}>
          <PendingEvacZonesError
            zoneNames={formState.errors.evacZoneStatuses.message}
            type={
              formState.errors.evacZoneStatuses
                .type as PendingReviewEvacZoneErrorType
            }
          />
        </Grid>
      )}

      {markerMoveCheckbox && <Grid size={12}>{markerMoveCheckbox}</Grid>}

      {isCriticalInitialReport && (
        <Grid size={12}>
          <CriticalAlertConfirmation
            messageFieldName="reportMessage"
            onConfirmationChange={setIsCriticalConfirmed}
          />
        </Grid>
      )}

      {submitButtons}
    </Grid>
  );

  return (
    <MapProvider>
      <FormProvider {...formMethods}>
        <MapFormLayout ref={scrollContainerRef}>
          <MapFormLayout.MapContent>
            <WildfireFormMap
              wildfire={wildfire}
              center={reviewSubmissionCenter}
              isEvacZonesLayerVisible={activeEvacuations}
              evacZones={evacZones}
              pendingEvacZones={pendingEvacZones}
              markerLock={markerLock}
              disableStructuredEvacZoneLayerClick={isProvisional}
            />
          </MapFormLayout.MapContent>
          <MapFormLayout.FormContent disablePadding={isInternalTabEnabled}>
            <FormWithoutEnter
              onSubmit={handleSubmit(handleSubmitGeoEvent)}
              noValidate
              onPaste={handlePaste}
            >
              {isInternalTabEnabled ? (
                <GeoEventFormTabs<WildfireFormValues>
                  idPrefix="wildfire-form"
                  internalFieldNames={[
                    'urgency',
                    'internalLinks',
                    'newReporterNote',
                  ]}
                  internalContent={
                    <InternalFormSection
                      key={getIncidentRemountKey('internal-form', wildfire)}
                      geoEventId={wildfire?.id}
                      newReporterNoteInitialValue={
                        initialValues.newReporterNote
                      }
                      showUrgency={!isPrescribed && !isRxOnlyReporter}
                      canEditUrgency={isWildfireStaffReporter}
                      showInternalLinks
                    />
                  }
                >
                  {formFields}
                </GeoEventFormTabs>
              ) : (
                formFields
              )}
            </FormWithoutEnter>
          </MapFormLayout.FormContent>
        </MapFormLayout>
      </FormProvider>
    </MapProvider>
  );
};
